'use client';

import { useEffect } from 'react';
import { useGetAccountFioVersionMigrationStartedAtQuery } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '../../contexts';
import { useAccountVersion } from '../../contexts/ActiveAccountIdContext/useAccountVersion';
import { appComponentsStorage } from '../../storage';
import { VIEWED_MIGRATED_ACCOUNTS_LOCAL_STORAGE_KEY } from '../constants';

type UseMigrationStartedAtParams = {
  accountId?: string;
  skip?: boolean;
};

/**
 * Gets the migration startedAt time for the current account.
 * This query is very fast because its data comes from Redis in massdriver.
 * When `migrationStartedAt === null` it means either:
 * - The account is a pre-existing V4 account and does not need to be migrated
 * - The account is a V3 account that hasn't begun migration
 */
export const useMigrationStartedAt = (
  params?: UseMigrationStartedAtParams
): string | undefined | null => {
  const activeAccountId = useActiveAccountId();
  const accountId = params?.accountId || activeAccountId;
  const accountVersion = useAccountVersion({ accountId });

  const { data } = useGetAccountFioVersionMigrationStartedAtQuery({
    variables: { accountId },
    skip: params?.skip || !accountId,
  });

  useEffect(() => {
    if (!data) return;
    const migrationStartedAt =
      data?.fioVersionMigration?.recentUpMigrationStartedAt;
    /**
     * If we are querying the migration start time but the start time is `null`, it means we have a pre-existing V4
     * account rather than a migrated account and we do not need to query this again, so store the current date in
     * local storage.
     */
    if (accountVersion && accountVersion >= 4 && migrationStartedAt === null) {
      const accountMigrationDates = appComponentsStorage.get(
        VIEWED_MIGRATED_ACCOUNTS_LOCAL_STORAGE_KEY
      );
      const newAccountMigrationDates = {
        ...accountMigrationDates,
        [accountId]: new Date().toISOString(),
      };

      appComponentsStorage.set(
        VIEWED_MIGRATED_ACCOUNTS_LOCAL_STORAGE_KEY,
        newAccountMigrationDates
      );
    }
    // Only fire when a new completed result arrives (mirrors `onCompleted`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const migrationStartedAt =
    data?.fioVersionMigration?.recentUpMigrationStartedAt;
  return migrationStartedAt;
};
